/**
 * Section Icons
 *
 * Shared SVG icon registry. Replaces the inline emoji used throughout the
 * app (🔥 ⚠️ 💨 🚒 📍 etc.) — Android system WebViews render emoji
 * inconsistently across vendors (Samsung vs Pixel vs OnePlus) and can fall
 * back to tofu boxes inside SVG <text>. Inline SVGs are pixel-perfect
 * everywhere, themeable via currentColor, and a/c lower bundle cost than
 * shipping the full Lucide package.
 *
 * Path data adapted from Lucide (https://lucide.dev/), MIT-licensed.
 *
 * Convention:
 *   - 24×24 viewBox
 *   - 2px stroke, round caps and joins
 *   - currentColor for stroke and fill
 *   - Optional `size` prop (default 20) and `className` / `style` pass-through
 */

import { memo, type SVGProps } from 'react';

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'size'> {
  /** Icon side length in pixels. Defaults to 20. */
  size?: number;
  /** Stroke width. Defaults to 2. */
  strokeWidth?: number;
}

const Svg = ({
  size = 20,
  strokeWidth = 2,
  children,
  ...rest
}: IconProps & { children: React.ReactNode }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...rest}
  >
    {children}
  </svg>
);

// ============================================================================
// Status & severity
// ============================================================================

/** ⚠️ replacement — triangle with exclamation. */
export const AlertTriangleIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <path d="M21.73 18a2 2 0 0 1-1.73 3H4a2 2 0 0 1-1.73-3L10.27 4a2 2 0 0 1 3.46 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </Svg>
));
AlertTriangleIcon.displayName = 'AlertTriangleIcon';

/** 🚨 replacement — siren. */
export const SirenIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <path d="M7 18v-6a5 5 0 1 1 10 0v6" />
    <path d="M5 21h14" />
    <path d="M21 12h1" />
    <path d="M18.5 4.5 18 5" />
    <path d="M2 12h1" />
    <path d="M12 2v1" />
    <path d="m4.929 4.929.707.707" />
    <path d="M12 12v6" />
  </Svg>
));
SirenIcon.displayName = 'SirenIcon';

/** ✅ replacement — check inside circle. */
export const CheckCircleIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </Svg>
));
CheckCircleIcon.displayName = 'CheckCircleIcon';

// ============================================================================
// Fire & emergency
// ============================================================================

/** 🔥 replacement — flame outline. Stroked, paired with severity colour. */
export const FlameIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
  </Svg>
));
FlameIcon.displayName = 'FlameIcon';

/** 🚒 replacement — fire truck. */
export const TruckIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <path d="M10 17h4V5H2v12h3" />
    <path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5" />
    <path d="M14 17h1" />
    <circle cx="7.5" cy="17.5" r="2.5" />
    <circle cx="17.5" cy="17.5" r="2.5" />
  </Svg>
));
TruckIcon.displayName = 'TruckIcon';

// ============================================================================
// Geo & wind
// ============================================================================

/** 📍 replacement — map pin. */
export const MapPinIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <path d="M20 10c0 7-8 13-8 13s-8-6-8-13a8 8 0 0 1 16 0z" />
    <circle cx="12" cy="10" r="3" />
  </Svg>
));
MapPinIcon.displayName = 'MapPinIcon';

/** 🗺️ replacement — folded map. */
export const MapIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
    <line x1="8" y1="2" x2="8" y2="18" />
    <line x1="16" y1="6" x2="16" y2="22" />
  </Svg>
));
MapIcon.displayName = 'MapIcon';

/** 💨 replacement — wind gust lines. */
export const WindIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2" />
    <path d="M9.6 4.6A2 2 0 1 1 11 8H2" />
    <path d="M12.6 19.4A2 2 0 1 0 14 16H2" />
  </Svg>
));
WindIcon.displayName = 'WindIcon';

/** 🏔️ replacement — mountain peaks. */
export const MountainIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <path d="m8 3 4 8 5-5 5 15H2L8 3z" />
  </Svg>
));
MountainIcon.displayName = 'MountainIcon';

/** 🌐 replacement — globe. */
export const GlobeIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </Svg>
));
GlobeIcon.displayName = 'GlobeIcon';

// ============================================================================
// Communication & notifications
// ============================================================================

/** 🔔 replacement — bell. */
export const BellIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </Svg>
));
BellIcon.displayName = 'BellIcon';

/** 💬 replacement — chat bubble. */
export const MessageCircleIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </Svg>
));
MessageCircleIcon.displayName = 'MessageCircleIcon';

/** 📰 replacement — newspaper. */
export const NewspaperIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2" />
    <path d="M18 14h-8" />
    <path d="M15 18h-5" />
    <path d="M10 6h8v4h-8V6z" />
  </Svg>
));
NewspaperIcon.displayName = 'NewspaperIcon';

/** 📋 replacement — clipboard list. */
export const ClipboardListIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <path d="M12 11h4" />
    <path d="M12 16h4" />
    <path d="M8 11h.01" />
    <path d="M8 16h.01" />
  </Svg>
));
ClipboardListIcon.displayName = 'ClipboardListIcon';

// ============================================================================
// Onboarding & exploration
// ============================================================================

/** 👋 replacement — compass for tour/exploration CTA. */
export const CompassIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="10" />
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
  </Svg>
));
CompassIcon.displayName = 'CompassIcon';

/** 🔍 replacement — search magnifier. */
export const SearchIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </Svg>
));
SearchIcon.displayName = 'SearchIcon';

/** 👁️ replacement — eye. */
export const EyeIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </Svg>
));
EyeIcon.displayName = 'EyeIcon';

/** ✏️ replacement — pencil. */
export const PencilIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </Svg>
));
PencilIcon.displayName = 'PencilIcon';

// ============================================================================
// Weather & conditions
// ============================================================================

/** 🛰️ replacement — satellite. */
export const SatelliteIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <path d="M13 7 9 3 5 7l4 4" />
    <path d="m17 11 4 4-4 4-4-4" />
    <path d="m8 12 4 4 6-6-4-4Z" />
    <path d="m16 8 3-3" />
    <path d="M9 21a6 6 0 0 0-6-6" />
  </Svg>
));
SatelliteIcon.displayName = 'SatelliteIcon';

/** 🌡️ replacement — thermometer. */
export const ThermometerIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <path d="M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0Z" />
  </Svg>
));
ThermometerIcon.displayName = 'ThermometerIcon';

/** ⚡ replacement — lightning bolt. */
export const ZapIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </Svg>
));
ZapIcon.displayName = 'ZapIcon';

/** 🚧 replacement — traffic cone. */
export const ConeIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <path d="M16.05 10.966 13 4.5a1 1 0 0 0-2 0L7.95 10.966" />
    <path d="m6.7 18.5 1.4-3.534" />
    <path d="m17.3 18.5-1.4-3.534" />
    <path d="M5 22h14" />
    <path d="M8.1 14.966h7.8" />
  </Svg>
));
ConeIcon.displayName = 'ConeIcon';

/** 📷 replacement — camera. */
export const CameraIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </Svg>
));
CameraIcon.displayName = 'CameraIcon';

/** 📊 replacement — bar chart. */
export const BarChartIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <line x1="12" y1="20" x2="12" y2="10" />
    <line x1="18" y1="20" x2="18" y2="4" />
    <line x1="6" y1="20" x2="6" y2="16" />
  </Svg>
));
BarChartIcon.displayName = 'BarChartIcon';

/** 🎮 replacement — gamepad / controls. */
export const GamepadIcon = memo((props: IconProps) => (
  <Svg {...props}>
    <line x1="6" y1="12" x2="10" y2="12" />
    <line x1="8" y1="10" x2="8" y2="14" />
    <line x1="15" y1="13" x2="15.01" y2="13" />
    <line x1="18" y1="11" x2="18.01" y2="11" />
    <rect x="2" y="6" width="20" height="12" rx="2" />
  </Svg>
));
GamepadIcon.displayName = 'GamepadIcon';
